import { Grid, styled, Tab, Tabs, Typography, Box, Card, CardContent, Avatar, Button } from "@mui/material";
import { Edit } from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import authActions from "../../redux/reducers/auth/actions";
import About from "../../components/About";
import EditProfile from "../EditProfile";
import ChangePassword from "../ChangePassword";
import PhoneVerificationModal from "../../components/PhoneVerificationModal";
import TwoFectorModal from "../../components/TwoFectorModal";
import NotificationSettings from "../NotificationSettings";
import Security from "../../components/Security";
import DocumentsManagement from "../../components/DocumentsManagement";
import Portfolio from "../../components/Portfolio";
import useStyles from "./styles";
import { useTokenValidation } from "../../components/hooks/useTokenValidation";
import SecurityPolicyModal from "../../components/SecurityPolicyModal";
import WelcomeBanner from "../EditProfile/components/WelcomeBanner";
import { useCRUDPermission } from "../../components/hooks/usePermission";
import Permission from "../../components/Permission";

const StyledHeading = styled("h2")({
  color: "#0D0D0E",
  fontFamily: "Inter Tight",
  fontSize: "24px",
  fontWeight: "500",
});

const StyledTab = styled(Tab)(({ theme, selected }) => ({
  color: selected ? "#fff !important" : "#949DAA",
  backgroundColor: selected ? "#0d0d0d" : "#fff",
  border: "1px solid #d7d8d9",
  borderRadius: "16px",
  minHeight: "40px !important",
  fontWeight: 500,
  textTransform: "none",
  marginRight: "10px",
  padding: "14px 8px !important",
  fontFamily: "Inter Tight",
  "&:hover": {
    backgroundColor: selected ? "#0d0d0d" : "",
    color: !selected && "#000",
  },
  "& .MuiTabs-indicator": {
    backgroundColor: "transparent !important",
  },
}));

export default function AccountSettings() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setAccountTab } = authActions;
  const { userData, accountTab } = useSelector((state) => state.auth);
  const { isWelcomeBannerDismissed } = useSelector((state) => state.common);
  const [tabValue, setTabValue] = useState(accountTab);
  const [showModal, setShowModal] = useState(false);
  const [show2FModal, setShow2FModal] = useState(false);

  // Token validation hook
  const { showSecurityModal, handleSecurityModalClose } = useTokenValidation();

  // Check PROFILE permissions
  const { hasPermission: hasProfileRead } = useCRUDPermission("PROFILE", "READ");
  const { hasPermission: hasProfileUpdate } = useCRUDPermission("PROFILE", "UPDATE");
  const { hasPermission: hasProfileCreate } = useCRUDPermission("PROFILE", "CREATE");
  const { hasPermission: hasProfileDelete } = useCRUDPermission("PROFILE", "DELETE");
  
  // Check USERS permissions
  const { hasPermission: hasUsersRead } = useCRUDPermission("USERS", "READ");
  
  // User can edit if they have any of: CREATE, UPDATE, or DELETE permissions
  const canEdit = hasProfileUpdate || hasProfileCreate || hasProfileDelete;
  
  // Security tab should only be visible to base "contractor" role
  const isBaseContractor = userData?.role?.toLowerCase() === "contractor";

  useEffect(() => {
    setTabValue(accountTab);
  }, [accountTab]);

  // Redirect if user doesn't have PROFILE READ permission
  useEffect(() => {
    if (!hasProfileRead && userData?.role) {
      navigate("/dashboard");
    }
  }, [hasProfileRead, userData, navigate]);

  // Reset tab if non-contractor tries to access Security tab (tabValue === 5)
  useEffect(() => {
    if (tabValue === 5 && !isBaseContractor) {
      setTabValue(0);
      dispatch(setAccountTab(0));
    }
  }, [tabValue, isBaseContractor, dispatch]);

  return (
    <>
      <Box
        sx={{
          typography: "body1",
          backgroundColor: "#fff",
          height: "100%",
          borderRadius: "6px",
          fontFamily: "Inter Tight",
        }}
      >

        <Grid item sx={{ padding: "0 2vw" }}>
          {/* Welcome Banner */}
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "4vh 2vw 2vh 2vw",
              background:
                "linear-gradient(180deg, rgba(77, 107, 246, 0.30) 0%, rgba(255, 255, 255, 0.00) 100%)",
            }}
          >
            <StyledHeading>Account Settings</StyledHeading>
          </div>
          {/* Profile Header - Always show */}
          <Grid item xs={12}>
          {!isWelcomeBannerDismissed && <WelcomeBanner />}

            <Card sx={{ mb: 3, borderRadius: 2, border: 'none', boxShadow: 'none' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  {/* Left Section - Avatar and Info */}
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Avatar
                      src={userData?.profile_url}
                      sx={{ width: 80, height: 80, mr: 3 }}
                    />
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 500, mb: 1, color: "#949DAA", fontSize: "24px" }}>
                        {userData?.contractor_data?.company_name || "CS Interior"}
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 500, mb: 1, color: "#000", fontSize: "32px" }}>
                        Welcome, {userData?.username || "User"}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#666" }}>
                        {userData?.role === "contractor" ? "Contractor" : userData?.role === "designer" ? "Designer" : "Supplier"} Member since {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Right Section - Business Info and Button */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                    {/* Employees Section */}
                    <Box sx={{ textAlign: "left" }}>
                      <Typography variant="body2" sx={{ color: "#949DAA", fontWeight: 400, fontSize: "14px", mb: 0.5 }}>
                        Employees
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 500, color: "#000" }}>
                        {userData?.contractor_data?.company_size ? userData.contractor_data.company_size.replace(/\s+(employees?|employee)$/i, '') : "11-50"}
                      </Typography>
                    </Box>

                    {/* Years in Business Section */}
                    <Box sx={{ textAlign: "left" }}>
                      <Typography variant="body2" sx={{ color: "#949DAA", fontWeight: 400, fontSize: "14px", mb: 0.5 }}>
                        Years in business
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 500, color: "#000" }}>
                        {userData?.contractor_data?.total_years_in_business ? userData.contractor_data.total_years_in_business.replace(/\s+(years?|year)$/i, '') : "4-7"}
                      </Typography>
                    </Box>

                    {/* Edit Profile Button - Only show if user has UPDATE permission */}
                    <Permission resource="PROFILE" action="UPDATE">
                      <Button
                        variant="contained"
                        startIcon={<Edit />}
                        onClick={() => navigate('/edit-profile-form')}
                        sx={{
                          backgroundColor: "#4D6BF6",
                          borderRadius: '16px',
                          textTransform: "none",
                          px: 3,
                          py: 1.5,
                          height: "fit-content",
                          ml: 6,
                        }}
                      >
                        Edit profile
                      </Button>
                    </Permission>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Navigation Tabs - Only show if user has PROFILE READ permission */}
          {hasProfileRead && (
            <Grid item xs={12}>
              <Tabs
                value={tabValue}
                onChange={(v, b) => {
                  if (b === 6) {
                    // Only navigate to Users tab if user has USERS READ permission
                    if (hasUsersRead) {
                      navigate("/account-setting/users");
                    }
                  } else {
                    setTabValue(b);
                    dispatch(setAccountTab(b));
                  }
                }}
                variant="scrollable"
                sx={{
                  "& .MuiTabs-indicator": {
                    background: "none",
                  },
                }}
              >
                <StyledTab label="Profile" selected={tabValue === 0} />
                <StyledTab label="Documents" selected={tabValue === 1} />
                <StyledTab label="Portfolio" selected={tabValue === 2} />
                {/* Notifications tab hidden */}
                {/* <StyledTab label="Notifications" selected={tabValue === 3} /> */}
                {userData?.social_connection === "google" ||
                userData?.social_connection === "fb" ||
                userData?.social_connection === "apple" ? null : (
                  <StyledTab label="Change Password" selected={tabValue === 4} />
                )}
                {/* Security tab - Only visible to base contractor role */}
                {isBaseContractor && (
                  <StyledTab label="Security" selected={tabValue === 5} />
                )}
                {hasUsersRead && (
                  <StyledTab label="Users" selected={tabValue === 6} />
                )}
              </Tabs>
            </Grid>
          )}

          {/* Tab Content - Only render if user has PROFILE READ permission */}
          {hasProfileRead && (
            <>
              {tabValue === 0 ? <EditProfile onTabChange={setTabValue} readOnly={!canEdit} /> : null}
              {tabValue === 1 ? <DocumentsManagement readOnly={!canEdit} /> : null}
              {tabValue === 2 ? <Portfolio readOnly={!canEdit} /> : null}
              {/* Notifications content hidden */}
              {/* {tabValue === 3 ? <NotificationSettings /> : null} */}
              {tabValue === 4 ? (
                userData?.social_connection === "google" ||
                userData?.social_connection === "fb" ||
                userData?.social_connection === "apple" ? (
                  <Security />
                ) : (
                  <ChangePassword />
                )
              ) : null}
              {/* Security tab content - Only visible to base contractor role */}
              {tabValue === 5 && isBaseContractor ? <Security /> : null}
            </>
          )}
        </Grid>
      </Box>

      {/* <About /> */}
      <PhoneVerificationModal
        visible={showModal}
        handleClose={() => setShowModal(false)}
      />
      <TwoFectorModal
        visible={show2FModal}
        handleClose={() => setShow2FModal(false)}
      />

      {/* Security Policy Modal */}
      <SecurityPolicyModal
        open={showSecurityModal}
        onConfirm={handleSecurityModalClose}
      />
    </>
  );
}
